import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../home/Loading';
import AllQuestions from '../AllData/AllQuestions';
import './Style/BookMarkedQuestions.css';
import Latex from 'react-latex';
import TopNavbar from '../home/TopNavbar';
import TopHeader from '../home/TopHeader';

const BookmarkedQuestions = () => {
    const [bookmarkedQuestions, setBookmarkedQuestions] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState('All'); 
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; 
    const questions = AllQuestions;

    useEffect(() => {
        const fetchBookmarkedQuestions = () => {
            try {
                const storedBookmarks = localStorage.getItem('questionBookmarks');
                if (storedBookmarks) {
                    const bookmarksData = JSON.parse(storedBookmarks);
                    setBookmarkedQuestions(bookmarksData);
                }
            } catch (err) {
                setError('Failed to load bookmarks from storage');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        
        fetchBookmarkedQuestions();
    }, []);

    useEffect(() => {
        if (selectedSubject === 'All') {
            setFilteredQuestions(bookmarkedQuestions);
        } else {
            setFilteredQuestions(bookmarkedQuestions.filter(bookmark => {
                const questionData = getQuestionData(bookmark.questionId.id);
                return questionData.subject === selectedSubject;
            }));
        }
        setCurrentPage(1); 
    }, [bookmarkedQuestions, selectedSubject]);

    const getQuestionData = (questionId) => {
        let idToFind;
        if (typeof questionId === 'object' && questionId.id) {
            idToFind = questionId.id;
        } else {
            idToFind = questionId;
        }
        
        return questions.find(q => {
            if (q.id && typeof q.id === 'object' && q.id) {
                return q.id === idToFind;
            } else {
                return q.id === idToFind;
            }
        }) || {};
    };

    const getSubjects = () => {
        const subjectsSet = new Set(['All']);
        bookmarkedQuestions.forEach(bookmark => {
            const questionData = getQuestionData(bookmark.questionId);
            if (questionData.subject) {
                subjectsSet.add(questionData.subject);
            }
        });
        return Array.from(subjectsSet);
    };

    const handleRemoveBookmark = (questionId) => {
        try {
            const updatedBookmarks = bookmarkedQuestions.filter(
                bookmark => {
                    let bookmarkQuestionId;
                    if (typeof bookmark.questionId === 'object' && bookmark.questionId.id) {
                        bookmarkQuestionId = bookmark.questionId.id;
                    } else {
                        bookmarkQuestionId = bookmark.questionId;
                    }
                    
                    let idToRemove;
                    if (typeof questionId === 'object' && questionId.id) {
                        idToRemove = questionId.id;
                    } else {
                        idToRemove = questionId;
                    }
                    
                    return bookmarkQuestionId !== idToRemove;
                }
            );
            
            localStorage.setItem('questionBookmarks', JSON.stringify(updatedBookmarks));
            setBookmarkedQuestions(updatedBookmarks);
        } catch (err) {
            setError('Failed to remove bookmark');
            console.error(err);
        }
    };

    const renderWithLatex = (text) => {
        if (!text) return null;
        if (text.includes('$') || text.includes('\\')) {
            return <Latex>{text}</Latex>;
        }
        return text;
    };

    if (loading) {
        return <Loading message='Loading bookmarked questions...' />
    }

    if (error) return <div className="bookmarked-questions-error-message">{error}</div>;

    const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);
    const currentQuestions = filteredQuestions
        .slice()
        .reverse()
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="bookmarked-questions-container">

            <TopNavbar name={"Bookmarked Questions " + "(" + filteredQuestions.length + ")"} />
            <TopHeader name={"Bookmarked Questions " + "(" + filteredQuestions.length + ")"} />

            <br></br> <br></br> <br></br>

            {filteredQuestions.length > 0 && (
                <div className="bookmarked-questions-filter-controls">
                    {getSubjects().map(subject => (
                        <button
                            key={subject}
                            className={`bookmarked-questions-filter-btn ${selectedSubject === subject ? 'active' : ''}`}
                            onClick={() => setSelectedSubject(subject)}
                        >
                            <p>{subject}</p>
                        </button>
                    ))}
                </div>
            )}
            
            {filteredQuestions.length === 0 ? (
                <div className="bookmarked-questions-empty-state">
                    <p className="bookmarked-questions-empty-message">
                        {selectedSubject === 'All'
                            ? "You haven't bookmarked any questions yet."
                            : `You haven't bookmarked any questions in ${selectedSubject}.`}
                    </p>
                    {selectedSubject === 'All' && (
                        <Link to="/practice-questions" className="bookmarked-questions-browse-link">
                            Start Practicing Now
                        </Link>
                    )}
                </div>
            ) : (
                <div className="bookmarked-questions-items-list">
                    {currentQuestions.map((bookmark, index) => {
                        const questionData = getQuestionData(bookmark.questionId);
                        const globalIndex = ((currentPage - 1) * itemsPerPage + index + 1);

                        return (
                            <div key={bookmark.id || bookmark.questionId} className="bookmarked-questions-item-card">

                                <div className="bookmarked-questions-item-content">
                                    {questionData.name && (
                                        <h4 className="bookmarked-questions-question-name">{questionData.name}</h4>
                                    )}
                                    {questionData.question_img ? (
                                        <div>
                                            <img
                                                src={`/questionsPhoto/${questionData.question_img}`}
                                                alt="Question"
                                                className="bookmarked-questions-item-image"
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="bookmarked-questions-item-text">⭐({globalIndex}) {renderWithLatex(questionData.question)}</p>
                                            <span className="bookmarked-questions-item-meta">
                                                {questionData.subject && `${questionData.subject}\n`}
                                                {questionData.topicname && `${questionData.topicname}\n`}
                                                {questionData.year && `NEET ${questionData.year}`}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="bookmarked-questions-item-actions">
                                    <button
                                        onClick={() => handleRemoveBookmark(bookmark.questionId)}
                                        className="bookmarked-questions-action-btn bookmarked-questions-remove-btn"
                                    >
                                        Remove
                                    </button>
                                    <Link
                                        to={`/question-solution/${
                                            typeof bookmark.questionId === 'object' && bookmark.questionId.id 
                                            ? bookmark.questionId.id 
                                            : bookmark.questionId
                                        }`}
                                        className="bookmarked-questions-action-btn bookmarked-questions-view-btn"
                                    >
                                        View Solution
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {filteredQuestions.length > itemsPerPage && (
                <div className="bookmarked-questions-pagination-controls">
                    <div className="bookmarked-questions-pagination-info">
                        Total: {filteredQuestions.length} | Page {currentPage} of {totalPages}
                    </div>
                    <div className="bookmarked-questions-pagination-buttons">
                        <button
                            className="bookmarked-questions-pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <button
                            className="bookmarked-questions-pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookmarkedQuestions;